/**
 * System Back closes a full-screen quiz instead of leaving the page.
 *
 * Opening pushes one history entry with the same URL; Back pops it and the layer
 * asks to close. Closing from the UI pops the entry itself — callers that navigate
 * right after closing wait for `settleFairQuizHistory()` so the pop does not land
 * on top of their `router.push`.
 */
import { nextTick } from 'vue'

const ENTRY_KEY = 'fairQuiz'

let pending: Promise<void> | null = null

export function pushFairQuizEntry(id: string): void {
  if (typeof window === 'undefined') return
  window.history.pushState({ ...window.history.state, [ENTRY_KEY]: id }, '')
}

export function isFairQuizEntryOnTop(id: string): boolean {
  if (typeof window === 'undefined') return false
  return window.history.state?.[ENTRY_KEY] === id
}

/** Pops the quiz entry if it is still on top; resolves once the browser has moved. */
export function popFairQuizEntry(id: string): Promise<void> {
  if (!isFairQuizEntryOnTop(id)) return pending ?? Promise.resolve()
  pending = new Promise<void>((resolve) => {
    const done = () => {
      window.removeEventListener('popstate', done)
      pending = null
      resolve()
    }
    window.addEventListener('popstate', done)
    window.history.back()
  })
  return pending
}

/**
 * Call after closing the quiz, before navigating: the layer pops its entry in a watcher,
 * so wait one tick for it to start, then for the browser to finish the move.
 */
export async function settleFairQuizHistory(): Promise<void> {
  await nextTick()
  await (pending ?? Promise.resolve())
}
